import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from '../constants';
import mermaid from 'mermaid';

// Initialize mermaid with dark theme and better box sizing
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  themeVariables: {
    primaryColor: '#0ea5e9',
    primaryTextColor: '#f1f5f9',
    primaryBorderColor: '#334155',
    lineColor: '#64748b',
    secondaryColor: '#1e293b',
    tertiaryColor: '#0f172a',
    fontSize: '14px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
    padding: 15,
    nodeSpacing: 50,
    rankSpacing: 60,
    diagramPadding: 20
  }
});

// Available roles for org chart
const AVAILABLE_ROLES = [
  { id: 'daglig_leder', label: 'Daglig leder', category: 'ledelse', required: true },
  { id: 'hms_ansvarlig', label: 'HMS-ansvarlig', category: 'hms' },
  { id: 'verneombud', label: 'Verneombud', category: 'hms' },
  { id: 'brannvernleder', label: 'Brannvernleder', category: 'hms' },
  { id: 'forstehjelp', label: 'Førstehjelpansvarlig', category: 'hms' },
  { id: 'miljoansvarlig', label: 'Miljøansvarlig', category: 'hms' },
  { id: 'kvalitetsansvarlig', label: 'Kvalitetsansvarlig', category: 'ledelse' },
  { id: 'avdelingsleder', label: 'Avdelingsleder(e)', category: 'ledelse' },
  { id: 'amu', label: 'AMU-representant', category: 'hms' },
  { id: 'bht', label: 'BHT-kontakt', category: 'hms' },
  { id: 'stoffkartotek', label: 'Stoffkartotek-ansvarlig', category: 'hms' },
  { id: 'adr', label: 'Sikkerhetsrådgiver (ADR)', category: 'hms' },
  { id: 'iso_ansvarlig', label: 'ISO-ansvarlig', category: 'sertifisering' },
  { id: 'miljofyrtarn', label: 'Miljøfyrtårn-kontakt', category: 'sertifisering' },
  { id: 'personvernombud', label: 'Personvernombud (GDPR)', category: 'sertifisering' },
];

const COMPANY_SIZES = [
  { id: 'small', label: 'Liten (1-10 ansatte)' },
  { id: 'medium', label: 'Mellomstor (10-50 ansatte)' },
  { id: 'large', label: 'Stor (50+ ansatte)' },
];

/**
 * OptionalAISupport - AI document generation with org chart customization
 */
function OptionalAISupport({ task, onDocumentGenerated }) {
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [svgContent, setSvgContent] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const mermaidRef = useRef(null);
  
  // Org chart options
  const [companySize, setCompanySize] = useState('small');
  const [selectedRoles, setSelectedRoles] = useState(['daglig_leder', 'hms_ansvarlig', 'verneombud', 'brannvernleder', 'forstehjelp']);
  
  // Check if this is an org chart task
  const isOrgChartTask = task.title?.toLowerCase().includes('organisasjonskart') || 
                          task.title?.toLowerCase().includes('org.kart') ||
                          task.title?.toLowerCase().includes('ansvarsfordeling');
  
  // Render mermaid diagram
  const renderMermaid = async (code) => {
    if (!code) return;
    try {
      const uniqueId = `mermaid-${Date.now()}`;
      const { svg } = await mermaid.render(uniqueId, code);
      setSvgContent(svg);
    } catch (error) {
      console.error('Mermaid rendering failed:', error);
      setSvgContent(null);
      toast.error('Kunne ikke vise diagram. Sjekk koden.');
    }
  };
  
  useEffect(() => {
    if (showContent && generatedContent?.content_type === 'mermaid' && !editMode) {
      renderMermaid(generatedContent.content);
    }
  }, [generatedContent, showContent, editMode]);
  
  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedContent(null);
    setSvgContent(null);
    setEditMode(false);
    
    try {
      const token = localStorage.getItem('systemklart_token');
      const requestBody = isOrgChartTask ? {
        company_size: companySize,
        selected_roles: selectedRoles.map(id => AVAILABLE_ROLES.find(r => r.id === id)?.label).filter(Boolean)
      } : {};
      
      const response = await axios.post(
        `${API}/ops/tasks/${task.id}/generate-document`,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setGeneratedContent(response.data);
        setEditedCode(response.data.content);
        setShowContent(true);
        setShowOptions(false);
        toast.success(response.data.content_type === 'mermaid' ? 'Organisasjonskart generert!' : 'Dokument generert!');
        if (onDocumentGenerated) onDocumentGenerated();
      }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Kunne ikke generere dokument');
    } finally {
      setGenerating(false);
    }
  };
  
  const toggleRole = (roleId) => {
    const role = AVAILABLE_ROLES.find(r => r.id === roleId);
    if (role?.required) return; // Can't deselect required roles
    
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };
  
  const handleUpdateDiagram = async () => {
    await renderMermaid(editedCode);
    setGeneratedContent(prev => ({ ...prev, content: editedCode }));
    setEditMode(false);
    toast.success('Diagram oppdatert!');
  };
  
  const copyToClipboard = () => {
    if (generatedContent?.content) {
      navigator.clipboard.writeText(generatedContent.content);
      toast.success('Kopiert til utklippstavlen');
    }
  };
  
  const downloadAsSvg = () => {
    if (svgContent) {
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organisasjonskart.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('SVG lastet ned!');
    }
  };
  
  const downloadAsPng = async () => {
    if (svgContent) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      img.onload = () => {
        canvas.width = img.width * 2;
        canvas.height = img.height * 2;
        ctx.scale(2, 2);
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          const pngUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = pngUrl;
          a.download = `organisasjonskart.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(pngUrl);
          toast.success('PNG lastet ned!');
        }, 'image/png');
        
        URL.revokeObjectURL(url);
      };
      
      img.src = url;
    }
  };
  
  // Group roles by category
  const rolesByCategory = {
    ledelse: AVAILABLE_ROLES.filter(r => r.category === 'ledelse'),
    hms: AVAILABLE_ROLES.filter(r => r.category === 'hms'),
    sertifisering: AVAILABLE_ROLES.filter(r => r.category === 'sertifisering'),
  };
  
  return (
    <div className="mb-5" data-testid="optional-ai-support">
      {!showContent ? (
        <>
          {/* Options panel for org chart */}
          {isOrgChartTask && showOptions && (
            <div className="mb-4 p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <h4 className="text-sm font-semibold text-white mb-3">Tilpass organisasjonskart</h4>
              
              {/* Company size */}
              <div className="mb-4">
                <label className="text-xs text-slate-400 mb-2 block">Bedriftsstørrelse</label>
                <div className="flex flex-wrap gap-2">
                  {COMPANY_SIZES.map(size => (
                    <button
                      key={size.id}
                      onClick={() => setCompanySize(size.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        companySize === size.id
                          ? 'bg-cyan-500 text-slate-950'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Role selection */}
              <div className="mb-4">
                <label className="text-xs text-slate-400 mb-2 block">Velg roller som skal inkluderes</label>
                
                {/* Ledelse */}
                <div className="mb-3">
                  <span className="text-xs text-slate-500 block mb-1">Ledelse</span>
                  <div className="flex flex-wrap gap-2">
                    {rolesByCategory.ledelse.map(role => (
                      <button
                        key={role.id}
                        onClick={() => toggleRole(role.id)}
                        disabled={role.required}
                        className={`px-2 py-1 rounded text-xs transition-all ${
                          selectedRoles.includes(role.id)
                            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                            : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                        } ${role.required ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        {role.label} {role.required && '✓'}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* HMS */}
                <div className="mb-3">
                  <span className="text-xs text-slate-500 block mb-1">HMS-roller</span>
                  <div className="flex flex-wrap gap-2">
                    {rolesByCategory.hms.map(role => (
                      <button
                        key={role.id}
                        onClick={() => toggleRole(role.id)}
                        className={`px-2 py-1 rounded text-xs transition-all ${
                          selectedRoles.includes(role.id)
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50'
                            : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Sertifisering */}
                <div>
                  <span className="text-xs text-slate-500 block mb-1">Sertifisering</span>
                  <div className="flex flex-wrap gap-2">
                    {rolesByCategory.sertifisering.map(role => (
                      <button
                        key={role.id}
                        onClick={() => toggleRole(role.id)}
                        className={`px-2 py-1 rounded text-xs transition-all ${
                          selectedRoles.includes(role.id)
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50'
                            : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Generate button */}
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full py-2.5 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50 transition-all"
              >
                {generating ? 'Genererer...' : `Generer organisasjonskart (${selectedRoles.length} roller)`}
              </button>
            </div>
          )}
          
          {/* Main button */}
          <button
            onClick={() => isOrgChartTask ? setShowOptions(!showOptions) : handleGenerate()}
            disabled={generating && !isOrgChartTask}
            className="w-full p-3 rounded-xl border border-slate-700 text-left hover:border-slate-600 hover:bg-slate-800/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center group-hover:bg-slate-700 transition-colors">
                {generating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500"></div>
                ) : isOrgChartTask ? (
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-200">
                  {isOrgChartTask 
                    ? (showOptions ? 'Skjul alternativer' : 'Lag organisasjonskart med AI')
                    : (generating ? 'Genererer...' : 'Har du ikke et dokument?')}
                </p>
                <p className="text-xs text-slate-500">
                  {isOrgChartTask 
                    ? 'Velg roller og generer visuelt diagram'
                    : 'AI kan lage et utkast for deg'}
                </p>
              </div>
              {isOrgChartTask && (
                <svg className={`w-5 h-5 text-slate-400 transition-transform ${showOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
          </button>
          
          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            AI-forslag må kvalitetssikres av deg
          </p>
        </>
      ) : (
        <div className="border border-slate-700 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="bg-slate-800 px-4 py-3 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-slate-200">
                {generatedContent?.content_type === 'mermaid' ? 'Organisasjonskart' : 'AI-generert utkast'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {generatedContent?.content_type === 'mermaid' && (
                <>
                  <button
                    onClick={() => { setEditMode(!editMode); setEditedCode(generatedContent.content); }}
                    className={`text-xs flex items-center gap-1 ${editMode ? 'text-amber-400' : 'text-slate-400 hover:text-slate-300'}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {editMode ? 'Redigerer' : 'Rediger'}
                  </button>
                  {svgContent && !editMode && (
                    <>
                      <button onClick={downloadAsPng} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        PNG
                      </button>
                      <button onClick={downloadAsSvg} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        SVG
                      </button>
                    </>
                  )}
                </>
              )}
              {!editMode && (
                <button onClick={copyToClipboard} className="text-xs text-slate-400 hover:text-slate-300 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Kopier
                </button>
              )}
              <button onClick={() => { setShowContent(false); setSvgContent(null); setEditMode(false); }} className="text-xs text-slate-500 hover:text-slate-300">
                Lukk
              </button>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="bg-amber-500/10 px-4 py-2 border-b border-amber-500/20">
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {generatedContent?.disclaimer || 'Erstatt [Navn] med faktiske ansvarlige i din virksomhet'}
            </p>
          </div>
          
          {/* Content */}
          <div className="p-4 max-h-[500px] overflow-auto bg-slate-900/50" ref={mermaidRef}>
            {editMode ? (
              <div>
                <p className="text-xs text-slate-400 mb-2">Rediger Mermaid-kode (du kan legge til/fjerne roller):</p>
                <textarea
                  value={editedCode}
                  onChange={(e) => setEditedCode(e.target.value)}
                  className="w-full h-64 bg-slate-800 border border-slate-600 rounded-lg p-3 text-sm text-slate-200 font-mono focus:outline-none focus:border-cyan-500"
                  spellCheck="false"
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleUpdateDiagram}
                    className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-medium hover:bg-cyan-400"
                  >
                    Oppdater diagram
                  </button>
                  <button
                    onClick={() => { setEditMode(false); setEditedCode(generatedContent.content); }}
                    className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            ) : generatedContent?.content_type === 'mermaid' && svgContent ? (
              <div 
                className="flex justify-center items-center min-h-[200px]"
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-300 leading-relaxed">
                {generatedContent?.content}
              </pre>
            )}
          </div>
          
          {/* Footer actions */}
          <div className="bg-slate-800 px-4 py-3 border-t border-slate-700 flex justify-between">
            <button
              onClick={() => { setShowContent(false); setShowOptions(true); }}
              className="text-sm text-slate-400 hover:text-slate-300 flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Endre roller
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyan-500"></div>
                  Genererer...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Generer på nytt
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OptionalAISupport;
